import { z } from 'zod'
import axios from 'axios'
import { useMutation, useQuery } from '@tanstack/react-query'
import fallbackPlaceholder from '../assets/fallbackPlaceholder.svg'

async function uploadAsset({
  file,
  model_field,
}: {
  file: File
  model_field: string
}) {
  const validatedData = UploadAssetSchema.parse({
    file: file,
    model_field: model_field,
  })
  const formData = new FormData()
  formData.append('model_field', validatedData.model_field)
  formData.append('asset', validatedData.file)

  const response = await axios.post('/api/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response.data.asset_id
}

export function useUploadAsset() {
  return useMutation({
    mutationFn: uploadAsset,
  })
}

async function getAsset({ asset_id }: { asset_id: number | null }) {
  const response = await axios.get(`/api/assets/${asset_id}`)
  return GetAssetSchema.parse(response.data)
}

export function useGetAsset({ asset_id }: { asset_id: number | null }) {
  return useQuery({
    queryKey: ['getAsset', { asset_id }],
    queryFn: () => getAsset({ asset_id }),
    enabled: !!asset_id,
    refetchInterval: (query) => {
      if (!query.state.data) return false
      return 'task_id' in query.state.data.main ? 500 : false
    },
  })
}

const FileSchema = z
  .instanceof(File)
  .refine(
    (file) =>
      ['image/jpeg', 'image/png', 'video/mp4', 'video/webm'].includes(
        file.type
      ),
    {
      message:
        'File must be image(JPEG and PNG supported) or video(MP4 or WEBM supported).',
    }
  )

const UploadAssetSchema = z.object({
  file: FileSchema,
  model_field: z.string(),
})

const GetAssetSchema = z.object({
  main: z.union([
    z.object({ task_id: z.number() }),
    z.object({ url: z.string() }),
  ]),
})

export const TeamLogoSchema = z.object({
  main: z
    .string()
    .url()
    .optional()
    .default(fallbackPlaceholder)
    .catch(fallbackPlaceholder),
  thumbnail: z
    .string()
    .url()
    .optional()
    .default(fallbackPlaceholder)
    .catch(fallbackPlaceholder),
  mini: z
    .string()
    .url()
    .optional()
    .default(fallbackPlaceholder)
    .catch(fallbackPlaceholder),
})

export type TeamLogo = z.infer<typeof TeamLogoSchema>
